import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useMediaQuery } from 'react-responsive';

import {
  useActiveSettlementMethodQuery,
  useSetDesiredActiveSettlementMethodMutation,
} from 'apollo/queries/autogenerate/hooks';
import { DesiredSettlementMethod } from 'apollo/queries/autogenerate/schemas';
import Button from 'shared/modules/common/components/atoms/Button';
import Dialog from 'shared/modules/common/components/atoms/Dialog';
import Icon from 'shared/modules/common/components/atoms/Icon';
import SuccessFail from 'shared/modules/common/components/organisms/SuccessFail';
import colors from 'shared/modules/common/constants/colors';
import { formatMonthDate } from 'shared/utils/date';

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  nextAutoDebitByDate: string;
  pullDate: string;
  isBillGenerated: boolean;
  billingCycle: string;
};

export default function ({
  open,
  onClose,
  onSuccess,
  nextAutoDebitByDate,
  pullDate,
  isBillGenerated,
  billingCycle,
}: Props) {
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const [
    setDesiredActiveSettlementMethod,
  ] = useSetDesiredActiveSettlementMethodMutation();

  const [displaySuccess, setDisplaySuccess] = useState(false);

  const {
    refetch: refetchActiveSettlementMethod,
  } = useActiveSettlementMethodQuery({
    variables: {
      billingCycle,
    },
    fetchPolicy: 'cache-and-network',
    skip: billingCycle === '',
  });

  useEffect(() => {
    let timeout: any;

    if (displaySuccess) {
      timeout = setTimeout(() => {
        refetchActiveSettlementMethod();
        setDisplaySuccess(false);
        onSuccess();
      }, 1000);
    }

    return () => clearInterval(timeout);
  }, [displaySuccess]);

  async function onUpdateButtonPress() {
    // ref: https://paidy-portal.atlassian.net/browse/UXFR-2326?focusedCommentId=306591
    const currentMonthBillingCycle = formatMonthDate(
      new Date().toDateString(),
      'YYYY-MM'
    );

    await setDesiredActiveSettlementMethod({
      variables: {
        billingCycle: isBillGenerated ? currentMonthBillingCycle : billingCycle,
        desiredSettlementMethod: DesiredSettlementMethod.AUTO_DEBIT,
      },
    });

    setDisplaySuccess(true);
  }

  return (
    <Dialog
      title="お支払い方法の変更"
      open={open}
      onClose={onClose}
      showCloseButton={true}
      onCloseButtonPress={onClose}
      style={style.root}
    >
      {displaySuccess ? (
        <SuccessFail
          success={true}
          message={{ success: 'お支払い方法の変更が完了しました。' }}
        />
      ) : (
        <>
          <View style={style.mainContainer}>
            <View style={style.topContainer}>
              <Text style={style.heading}>
                口座振替（自動引落）に変更しますか？
              </Text>
            </View>
            <View style={style.highlightedArea}>
              <Icon style={style.icon} name="calendar" size="20" />
              <Text style={style.text}>
                {isBillGenerated
                  ? '来月分のご請求金額のお引き落とし日'
                  : 'お引き落とし日'}
              </Text>

              <Text style={style.highlightedText}>{pullDate}</Text>
            </View>
            <View style={style.textContainer}>
              <Text style={style.text}>
                {isBillGenerated
                  ? `来月分のお引き落としは、${nextAutoDebitByDate}までにご利用明細に反映されます。`
                  : `${nextAutoDebitByDate}までにご利用明細に反映されます。`}
                {'\n'}
                {'\n'}
                お引き落としがご利用明細に反映されるのは、金融機関から当社に情報が到着してからとなります。数日お時間がかかりますので、ご了承ください。
              </Text>
            </View>

            <View
              style={[
                style.buttonContainer,
                isBigScreen && style.buttonGroupBigScreen,
              ]}
            >
              <Button
                variant="primary"
                title="変更する"
                onPress={onUpdateButtonPress}
                style={[style.button, isBigScreen && style.buttonBigScreen]}
              />
            </View>
          </View>
        </>
      )}
    </Dialog>
  );
}

const style = StyleSheet.create({
  root: {
    width: '100%',
    maxHeight: 624,
  },
  text: {
    fontSize: 16,
    fontWeight: '300',
    lineHeight: 24,
    color: colors.greyDark,
    marginBottom: 20,
  },
  heading: {
    fontSize: 20,
    lineHeight: 24,
    fontWeight: '600',
    textAlign: 'center',
  },
  highlightedArea: {
    flex: 4,
    backgroundColor: colors.greySuperLight,
    borderRadius: 16,
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 20,
  },

  buttonGroupBigScreen: {
    flexDirection: 'row',
    marginHorizontal: -8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  button: {
    alignSelf: 'center',
  },
  buttonBigScreen: {
    marginHorizontal: 8,
    marginBottom: 0,
  },
  highlightedText: {
    fontSize: 16,
    color: colors.greyDark,
    fontWeight: '600',
  },
  icon: {
    fontWeight: '600',
    marginBottom: 20,
  },
  mainContainer: {
    flex: 1,
  },
  topContainer: {
    flex: 1,
  },
  textContainer: {
    flex: 4,
  },
  buttonContainer: {
    flex: 1,
  },
});
